import type { DateFact } from '../types/document'

// "Add reminder to calendar": builds an .ics file on the phone for a date found
// in the document. Nothing is sent anywhere; the phone's calendar app opens it.

/** Dates worth a reminder: things the person has to act on or be present for. */
export const REMINDER_KINDS: DateFact['kind'][] = ['deadline', 'due', 'hearing', 'validity']

function escapeText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/[,;]/g, (c) => `\\${c}`)
}

/** Folds a content line at 75 octets without splitting a character (RFC 5545 §3.1). */
export function foldLine(line: string): string {
  const encoder = new TextEncoder()
  const parts: string[] = []
  let current = ''
  let bytes = 0
  for (const char of line) {
    const size = encoder.encode(char).length
    const limit = parts.length === 0 ? 75 : 74 // continuation lines start with a space
    if (bytes + size > limit) {
      parts.push(current)
      current = ''
      bytes = 0
    }
    current += char
    bytes += size
  }
  parts.push(current)
  return parts.join('\r\n ')
}

const compact = (iso: string) => iso.replace(/-/g, '')

function nextDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d + 1))
  return date.toISOString().slice(0, 10)
}

export function buildIcs(event: { iso: string; title: string; description: string; kind: DateFact['kind'] }, now = new Date()): string {
  const stamp = now.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
  const uid = `${compact(event.iso)}-${Math.random().toString(36).slice(2, 10)}@samjho.pages.dev`
  // Deadlines get a week's and a day's notice; other dates a day's notice
  const alarms = (event.kind === 'validity' ? ['-P7D'] : event.kind === 'deadline' || event.kind === 'due' ? ['-P7D', '-P1D'] : ['-P1D']).flatMap(
    (trigger) => ['BEGIN:VALARM', 'ACTION:DISPLAY', `TRIGGER:${trigger}`, `DESCRIPTION:${escapeText(event.title)}`, 'END:VALARM']
  )
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Samjho//Document reminders//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${compact(event.iso)}`,
    `DTEND;VALUE=DATE:${compact(nextDay(event.iso))}`,
    `SUMMARY:${escapeText(event.title)}`,
    `DESCRIPTION:${escapeText(event.description)}`,
    'TRANSP:TRANSPARENT',
    ...alarms,
    'END:VEVENT',
    'END:VCALENDAR'
  ]
  return lines.map(foldLine).join('\r\n') + '\r\n'
}

/** Opens the .ics with the phone's calendar (share sheet when available, otherwise a download). */
export async function openInCalendar(ics: string, iso: string): Promise<void> {
  const name = `samjho-${iso}.ics`
  const file = new File([ics], name, { type: 'text/calendar' })
  try {
    if (navigator.canShare?.({ files: [file] }) && /android|iphone|ipad/i.test(navigator.userAgent)) {
      await navigator.share({ files: [file] })
      return
    }
  } catch {
    // share sheet dismissed or unsupported: fall back to a download
  }
  const url = URL.createObjectURL(file)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
